/**
 * Plans home — commitment cockpit.
 *
 * Situational awareness of Authorized (PoR), Candidate, and Hypothesis lanes.
 * Feasibility work stays in Planning Engine; this surface does not mount the workbench.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  FlaskConical,
  Layers,
  Loader2,
  Rocket,
  Shield,
  Target,
} from 'lucide-react';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { listPlanCycles, getActivePlanOfRecord } from '../../lib/planningService';
import { getScenarios, activateScenario } from '../../lib/planningEngineService';
import type { Scenario } from '../../lib/planningEngineModule.types';
import type { PlanCycle } from '../../types/planning';
import {
  AUTHORIZED_LANE_BADGE_CLASS,
  COMMITMENT_LANE_LABEL,
  deriveScenarioLane,
  partitionScenariosByLane,
  SCENARIO_LANE_BADGE_CLASS,
  SCENARIO_LANE_LABEL,
} from '../../lib/planningScenarioLanes';
import { useTimePhasedPlanningEnabled } from '../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import {
  formatProductShellDateLabel,
  greetingForHour,
  PRODUCT_SHELL_DATE_CLASS,
  PRODUCT_SHELL_SUBTITLE_CLASS,
  PRODUCT_SHELL_TITLE_CLASS,
} from '../../lib/productShellPageStyles';

const CARD =
  ' border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]';

type PorRow = {
  cycle: PlanCycle;
  versionLabel: string | null;
  authorizedAt: string | null;
};

function LaneChip({
  lane,
}: {
  lane: 'authorized' | 'candidate' | 'hypothesis';
}) {
  const cls =
    lane === 'authorized'
      ? AUTHORIZED_LANE_BADGE_CLASS
      : SCENARIO_LANE_BADGE_CLASS[lane];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5  border text-[11px] font-mono tracking-wide uppercase ${cls}`}
    >
      {COMMITMENT_LANE_LABEL[lane]}
    </span>
  );
}

export default function PlanningCommitmentCockpit() {
  const navigate = useNavigate();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceName = useTenantStore((s) => {
    const id = s.currentWorkspaceId;
    return id ? s.workspaces.find((w) => w.id === id)?.name : undefined;
  });
  const timePhasedEnabled = useTimePhasedPlanningEnabled();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pors, setPors] = useState<PorRow[]>([]);
  const [cycles, setCycles] = useState<PlanCycle[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const [{ data: cycleList, error: cycleErr }, scenarioList] = await Promise.all([
          listPlanCycles({ workspaceId }),
          getScenarios(workspaceId),
        ]);
        if (cancelled) return;
        if (cycleErr) throw new Error(cycleErr);

        const activeCycles = (cycleList ?? []).filter(
          (c) => c.status === 'active' || c.status === 'draft',
        );
        setCycles(activeCycles);
        setScenarios(scenarioList);

        const porRows: PorRow[] = [];
        await Promise.all(
          activeCycles.map(async (cycle) => {
            const { data: por } = await getActivePlanOfRecord(cycle.id);
            if (!por) return;
            porRows.push({
              cycle,
              versionLabel:
                por.plan_version != null
                  ? `v${por.plan_version.version_number}`
                  : por.plan_version_id?.slice(0, 8) ?? null,
              authorizedAt: por.authorized_at ?? null,
            });
          }),
        );
        if (!cancelled) setPors(porRows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load commitment status');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const { candidates, hypotheses } = useMemo(
    () => partitionScenariosByLane(scenarios),
    [scenarios],
  );

  const openInEngine = async (scenario: Scenario) => {
    if (!workspaceId) return;
    setActivatingId(scenario.id);
    try {
      if (!scenario.active) {
        await activateScenario(scenario.id, workspaceId);
      }
      navigate('/planning/engine');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open scenario in Engine');
    } finally {
      setActivatingId(null);
    }
  };

  const greeting = greetingForHour();
  const dateLabel = formatProductShellDateLabel();

  if (!workspaceId) {
    return (
      <div className="px-6 py-10 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Select a workspace to see commitment status.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading commitment status…
      </div>
    );
  }

  return (
    <div className="px-6 pb-10 pt-2 max-w-5xl mx-auto w-full space-y-8">
      <header>
        <p className={PRODUCT_SHELL_DATE_CLASS}>{dateLabel}</p>
        <h1 className={PRODUCT_SHELL_TITLE_CLASS}>
          {greeting}
          {workspaceName ? (
            <>
              , <span className="text-primary-600 dark:text-primary-400">{workspaceName.replace(/\s*Workspace$/i, '')}</span>
            </>
          ) : null}
          .
        </h1>
        <p className={PRODUCT_SHELL_SUBTITLE_CLASS}>
          Commitment status — what is authorized, what is still a candidate, and which what-ifs are in
          flight. Two capabilities share scenario and scope: Viability (rough-cut) and Launch planning
          (time-phased execution).
        </p>
      </header>

      {error && (
        <div className="border border-[var(--coral)] dark:border-[var(--coral)]/50 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/30 px-4 py-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {/* Two capabilities */}
      <div className={`grid gap-3 ${timePhasedEnabled ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        <div className={`${CARD} p-5 flex flex-col gap-3`}>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            <h2 className="text-sm font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Viability
            </h2>
            <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)]">
              Rough-cut
            </span>
          </div>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
            Confirm the candidate set is realistic in WU — earlier confirmation before detailed planning.
          </p>
          <Link
            to="/planning/engine"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            Open Viability
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {timePhasedEnabled && (
          <div className={`${CARD} p-5 flex flex-col gap-3 border-[var(--slate)] dark:border-[var(--slate)]/40`}>
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              <h2 className="text-sm font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Launch planning
              </h2>
              <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)]">
                Time-phased
              </span>
            </div>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
              Detailed execution planning — place launches on shared teams with supply and funding.
            </p>
            <Link
              to="/planning/launch-calendar"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[var(--slate)] dark:border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)] text-sm font-medium hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20"
            >
              Open Launch Calendar
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>

      {/* Authorized — PoR */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)]" />
          <h2 className="text-sm font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Authorized
          </h2>
          <LaneChip lane="authorized" />
        </div>
        {pors.length === 0 ? (
          <div className={`${CARD} p-5`}>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              No Plan of Record is authorized yet. Shape a candidate in the Engine, promote it to a plan cycle, then authorize.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/planning/plans"
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
              >
                Plan of Record
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {pors.map(({ cycle, versionLabel, authorizedAt }) => (
              <li key={cycle.id}>
                <Link
                  to={`/planning/plans/${cycle.id}`}
                  className={`${CARD} p-4 flex items-center gap-3 hover:bg-[var(--hover-tint)] transition-colors`}
                >
                  <CheckCircle2 className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                        {cycle.name}
                      </span>
                      <LaneChip lane="authorized" />
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                      Active Plan of Record
                      {versionLabel ? ` · ${versionLabel}` : ''}
                      {authorizedAt
                        ? ` · ${new Date(authorizedAt).toLocaleDateString()}`
                        : ''}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* In flight — Candidates + Hypotheses */}
      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Candidates
            </h2>
            <LaneChip lane="candidate" />
          </div>
          {candidates.length === 0 ? (
            <div className={`${CARD} p-4 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]`}>
              No working models yet. Create a scenario in the Engine (cycle-linked or free exploration).
            </div>
          ) : (
            <ul className="space-y-2">
              {candidates.slice(0, 8).map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    disabled={activatingId === s.id}
                    onClick={() => void openInEngine(s)}
                    className={`${CARD} w-full p-3.5 text-left flex items-center gap-3 hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-60`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                          {s.name}
                        </span>
                        {s.active && (
                          <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                        {s.planningWeeks} wk · {Math.round(s.capacityBufferPercent * 100)}% buffer
                        {s.planCycleId ? ' · cycle-linked' : ' · free exploration'}
                      </p>
                    </div>
                    {activatingId === s.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
                    ) : (
                      <ArrowRight className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {candidates.length > 8 && (
            <Link
              to="/planning/engine/scenarios"
              className="mt-2 inline-block text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              View all {candidates.length} candidates
            </Link>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <FlaskConical className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)]" />
            <h2 className="text-sm font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Hypotheses
            </h2>
            <LaneChip lane="hypothesis" />
          </div>
          {hypotheses.length === 0 ? (
            <div className={`${CARD} p-4 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]`}>
              No what-ifs yet. From Launch Calendar, Delay or Promote creates a hypothesis you can Compare.
            </div>
          ) : (
            <ul className="space-y-2">
              {hypotheses.slice(0, 8).map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    disabled={activatingId === s.id}
                    onClick={() => void openInEngine(s)}
                    className={`${CARD} w-full p-3.5 text-left flex items-center gap-3 hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-60`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                          {s.name}
                        </span>
                        <span
                          className={`text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border ${SCENARIO_LANE_BADGE_CLASS.hypothesis}`}
                        >
                          {SCENARIO_LANE_LABEL[deriveScenarioLane(s)]}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                        Forked what-if — open in Engine or Compare
                      </p>
                    </div>
                    {activatingId === s.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
                    ) : (
                      <ArrowRight className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {hypotheses.length > 0 && (
            <Link
              to="/planning/engine/compare"
              className="mt-2 inline-block text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              Compare scenarios
            </Link>
          )}
        </div>
      </section>

      {/* Secondary links */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Link
          to="/planning/plans"
          className={`${CARD} p-4 hover:bg-[var(--hover-tint)] transition-colors`}
        >
          <ClipboardList className="h-4 w-4 text-[var(--text-ter)] mb-2" />
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Plan of Record</p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
            {cycles.length} cycle{cycles.length === 1 ? '' : 's'} · communicate the commitment
          </p>
        </Link>
        {timePhasedEnabled && (
          <Link
            to="/planning/launch-calendar"
            className={`${CARD} p-4 hover:bg-[var(--hover-tint)] transition-colors`}
          >
            <Rocket className="h-4 w-4 text-primary-600 dark:text-primary-400 mb-2" />
            <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Launch Calendar</p>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              Buildable vs launchable on shared teams
            </p>
          </Link>
        )}
        <Link
          to="/planning/engine/compare"
          className={`${CARD} p-4 hover:bg-[var(--hover-tint)] transition-colors`}
        >
          <FlaskConical className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)] mb-2" />
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Compare</p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
            Side-by-side candidates and hypotheses
          </p>
        </Link>
      </section>

      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        Prefer the older live-items or feasibility dashboards?{' '}
        <Link to="/planning/dashboard?view=live" className="text-primary-600 dark:text-primary-400 hover:underline">
          Live items
        </Link>
        {' · '}
        <Link to="/planning/dashboard?view=feasibility" className="text-primary-600 dark:text-primary-400 hover:underline">
          Feasibility
        </Link>
      </p>
    </div>
  );
}
